import {
  HttpErrorResponse,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, of, throwError, NEVER } from 'rxjs';
import { catchError, switchMap, delay } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { SignatureHeaders, SignatureService } from './signature.service';

// Interceptor que adiciona x-timestamp e x-signature em todas as requisições
// para a API do backend (exceto /tempo). 
@Injectable()
export class SignatureInterceptor implements HttpInterceptor {
  private readonly apiBase = environment.URL_API;

  constructor(
    private signatureService: SignatureService,
    private router: Router
  ) { }

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<any> {
    const url = req.urlWithParams ?? req.url;

    let request$: Observable<any>;

    if (!this.isApiRequest(url) || this.isTempoRequest(url) || !this.isLogado()) {
      request$ = next.handle(req);
    } else {
      const method = req.method;
      const urlParaAssinar = url.split('?')[0];
      request$ = this.signAndSend(req, method, urlParaAssinar, next).pipe(
        catchError((error: unknown) => {
          if (this.deveReenviar(error)) {
            return of(null).pipe(
              delay(500),
              switchMap(() => this.signAndSend(req, method, urlParaAssinar, next))
            );
          }
          return throwError(error);
        })
      );
    }

    return request$.pipe(
      catchError((error: unknown) => {
        if (error instanceof HttpErrorResponse && (error.status === 401 || error.status === 403)) {
          // Não redireciona se for uma tentativa de login (para permitir mostrar "Usuário ou senha incorretos")
          if (!this.isLoginRequest(url)) {
            this.lidarComNaoAutorizado();
            return NEVER;
          }
        }
        return throwError(error);
      })
    );
  }

  private signAndSend(
    req: HttpRequest<unknown>,
    method: string,
    url: string,
    next: HttpHandler
  ): Observable<any> {
    return this.signatureService.ensureSyncAndGetHeaders(method, url).pipe(
      switchMap(headers => next.handle(this.buildSignedRequest(req, headers)))
    );
  }

  private buildSignedRequest(
    req: HttpRequest<unknown>,
    headers: SignatureHeaders
  ): HttpRequest<unknown> {
    return req.clone({
      setHeaders: {
        'x-timestamp': headers['x-timestamp'],
        'x-signature': headers['x-signature']
      }
    });
  }

  private isApiRequest(url: string): boolean {
    return url.startsWith(this.apiBase);
  }

  private isTempoRequest(url: string): boolean {
    const path = url.replace(this.apiBase, '');
    return path === '/tempo' || path.startsWith('/tempo?');
  }

  private isLogado(): boolean {
    try {
      const item = JSON.parse(sessionStorage.getItem('currentUser'));
      return !!item?.token;
    } catch {
      return false;
    }
  }

  private deveReenviar(error: unknown): boolean {
    if (!(error instanceof HttpErrorResponse)) {
      return true;
    }

    if (error.status === 0) {
      return true;
    }

    return false;
  }

  private lidarComNaoAutorizado() {
    sessionStorage.clear();
    this.router.navigate(['login']);
  }

  private isLoginRequest(url: string): boolean {
    const path = url.replace(this.apiBase, '');
    return path === '/admin/login' || path === '/admin' || path.startsWith('/admin/login?') || path.startsWith('/admin?');
  }
}
